import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const PRIORITIES = ["Direct", "HOT", "Rush", "Regular", "Sameday", "NextDay"];

const today = () => new Date().toISOString().slice(0, 10);

export default function CustomerShipmentForm({ onCreated }) {
  const [f, setF] = useState({
    po_number: "", booking_date: today(), delivery_date: today(),
    service_priority: "Regular", weight_kg: 1, pieces: 1,
    package_description: "", special_instructions: "",
    pickup_addr: "", pickup_name: "", pickup_phone: "", pickup_instr: "", pickup_lat: null, pickup_lng: null,
    dropoff_addr: "", dropoff_name: "", dropoff_phone: "", dropoff_instr: "", dropoff_lat: null, dropoff_lng: null,
  });
  const [busy, setBusy] = useState(false);

  const submit = async (ev) => {
    ev.preventDefault();
    setBusy(true);
    try {
      const payload = {
        account_id: "self", // ignored by backend; it forces the customer's own account_id
        po_number: f.po_number || null,
        booking_date: f.booking_date, delivery_date: f.delivery_date,
        service_priority: f.service_priority,
        pickup: { address: f.pickup_addr, contact_name: f.pickup_name, contact_phone: f.pickup_phone, instructions: f.pickup_instr, lat: f.pickup_lat, lng: f.pickup_lng },
        dropoff: { address: f.dropoff_addr, contact_name: f.dropoff_name, contact_phone: f.dropoff_phone, instructions: f.dropoff_instr, lat: f.dropoff_lat, lng: f.dropoff_lng },
        weight_kg: Number(f.weight_kg), pieces: Number(f.pieces),
        package_description: f.package_description, special_instructions: f.special_instructions,
        pricing: {}, // backend will default pricing until the pricing engine is wired
      };
      const { data } = await api.post("/deliveries", payload);
      toast.success(`Shipment booked — ${data.delivery.delivery_number}`, { duration: 5000 });
      onCreated?.(data.delivery);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const Field = ({ label, children }) => (
    <div className="space-y-1"><Label className="font-mono text-[11px] uppercase tracking-widest text-slate-700">{label}</Label>{children}</div>
  );
  const cls = "h-11 bg-slate-100 border border-slate-300 focus-visible:border-brand-green focus-visible:ring-brand-green/30 font-mono text-sm text-foreground placeholder:text-slate-400";

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="customer-shipment-form">
      <div className="grid grid-cols-3 gap-3">
        <Field label="PO #"><Input value={f.po_number} onChange={(e)=>setF({...f,po_number:e.target.value})} className={cls} data-testid="po-input"/></Field>
        <Field label="Booking Date"><Input type="date" required value={f.booking_date} onChange={(e)=>setF({...f,booking_date:e.target.value})} className={cls}/></Field>
        <Field label="Delivery Date"><Input type="date" required value={f.delivery_date} onChange={(e)=>setF({...f,delivery_date:e.target.value})} className={cls}/></Field>
      </div>

      <Field label="Service Priority">
        <Select value={f.service_priority} onValueChange={(v) => setF({ ...f, service_priority: v })}>
          <SelectTrigger className={cls} data-testid="priority-select"><SelectValue/></SelectTrigger>
          <SelectContent>{PRIORITIES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
        </Select>
      </Field>

      <div className="border border-slate-300 bg-slate-50 p-3 space-y-2">
        <div className="font-mono text-[10px] uppercase tracking-widest text-brand-success">↑ Pickup</div>
        <AddressAutocomplete
          required
          value={f.pickup_addr}
          onChange={(addr, ex) => setF({ ...f, pickup_addr: addr, pickup_lat: ex.lat ?? null, pickup_lng: ex.lng ?? null })}
          placeholder="Business name or address"
          testId="pickup-address"
        />
        <div className="grid grid-cols-2 gap-2">
          <Input placeholder="Contact Name" value={f.pickup_name} onChange={(e)=>setF({...f,pickup_name:e.target.value})} className={cls}/>
          <Input placeholder="Contact Phone" value={f.pickup_phone} onChange={(e)=>setF({...f,pickup_phone:e.target.value})} className={cls}/>
        </div>
        <Input placeholder="Pickup instructions" value={f.pickup_instr} onChange={(e)=>setF({...f,pickup_instr:e.target.value})} className={cls}/>
      </div>

      <div className="border border-slate-300 bg-slate-50 p-3 space-y-2">
        <div className="font-mono text-[10px] uppercase tracking-widest text-brand-orange">↓ Delivery</div>
        <AddressAutocomplete
          required
          value={f.dropoff_addr}
          onChange={(addr, ex) => setF({ ...f, dropoff_addr: addr, dropoff_lat: ex.lat ?? null, dropoff_lng: ex.lng ?? null })}
          placeholder="Business name or address"
          testId="dropoff-address"
        />
        <div className="grid grid-cols-2 gap-2">
          <Input placeholder="Contact Name" value={f.dropoff_name} onChange={(e)=>setF({...f,dropoff_name:e.target.value})} className={cls}/>
          <Input placeholder="Contact Phone" value={f.dropoff_phone} onChange={(e)=>setF({...f,dropoff_phone:e.target.value})} className={cls}/>
        </div>
        <Input placeholder="Delivery instructions" value={f.dropoff_instr} onChange={(e)=>setF({...f,dropoff_instr:e.target.value})} className={cls}/>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Field label="Weight (kg)"><Input type="number" step="0.1" min="0" required value={f.weight_kg} onChange={(e)=>setF({...f,weight_kg:e.target.value})} className={cls} data-testid="weight-input"/></Field>
        <Field label="Pieces"><Input type="number" min="1" required value={f.pieces} onChange={(e)=>setF({...f,pieces:e.target.value})} className={cls}/></Field>
        <Field label="Package Desc"><Input value={f.package_description} onChange={(e)=>setF({...f,package_description:e.target.value})} className={cls}/></Field>
      </div>
      <Field label="Special Instructions"><Textarea value={f.special_instructions} onChange={(e)=>setF({...f,special_instructions:e.target.value})} className="bg-slate-100 border border-slate-300 focus-visible:border-brand-green focus-visible:ring-brand-green/30 font-mono text-sm placeholder:text-slate-400" data-testid="special-instr"/></Field>

      <Button type="submit" disabled={busy} className="w-full h-12 bg-brand-green text-black hover:bg-brand-green/90 font-mono uppercase tracking-wider font-bold" data-testid="submit-shipment-btn">
        {busy ? "Booking..." : "Book Shipment"}
      </Button>
    </form>
  );
}
